// @flow

import * as React from 'react';
import { graphql, useFragment } from 'react-relay';
import { Flex, Box, useSystem } from 'react-system';
import { GoogleMap, GoogleMarker } from '@realadvisor/google-maps';
import { useLocale } from '../../hooks/locale';
import { useTheme } from '../../theme';
import type { DevelopmentLocation_development$key } from './__generated__/DevelopmentLocation_development.graphql';

type Props = {|
  development: DevelopmentLocation_development$key,
|};

const RatioContainer = ({ ratio, children }) => {
  return (
    <Box pt={ratio.map(r => r * 100 + '%')} css={{ position: 'relative' }}>
      <Box css={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}>
        {children}
      </Box>
    </Box>
  );
};

export const DevelopmentLocation = (props: Props): React.Node => {
  const { text } = useTheme();
  const { t } = useLocale();
  const { media } = useSystem();

  const development = useFragment(
    graphql`
      fragment DevelopmentLocation_development on DevelopmentProduct {
        products {
          property {
            lat
            lng
            locality
            postcode
            route
            streetNumber
          }
        }
      }
    `,
    props.development,
  );

  const products = development.products ?? [];
  const property = products.length === 0 ? null : products[0].property;

  if (property == null) {
    return null;
  }

  const marker = {
    lat: property.lat ?? 0,
    lng: property.lng ?? 0,
  };

  return (
    <>
      <Box py={3} css={media([text.h6, text.h5])}>
        {t('location')}
      </Box>
      <Flex flexWrap="wrap" justifyContent="flex-start">
        {property.postcode != null && (
          <Box width={[1, 1 / 2]} css={{ maxWidth: 360 }} pb={3}>
            <p css={text.subtitle1}>
              {property.postcode} {property.locality}
            </p>
            <p css={text.body1}>{t('postalCode')}</p>
          </Box>
        )}
        {property.route != null && (
          <Box width={[1, 1 / 2]} pb={3}>
            <h1 css={text.subtitle1}>
              {[property.route, ' ', property.streetNumber].join('')}
            </h1>
            <p css={text.body1}>{t('address')}</p>
          </Box>
        )}
      </Flex>
      <RatioContainer ratio={[1, 1, 0.6]}>
        <GoogleMap center={marker} defaultZoom={13} mapTypeId="roadmap">
          <GoogleMarker lat={marker.lat} lng={marker.lng} />
        </GoogleMap>
      </RatioContainer>
    </>
  );
};
